import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type TransitionEvent,
} from 'react';
import {
  HiChevronDoubleLeft,
  HiChevronDoubleRight,
  HiChevronDown,
  HiChevronLeft,
  HiChevronRight,
} from 'react-icons/hi';

import { AvailableViewSummary } from 'domain/surveyDataManager/contracts/availableViews';
import { RecipeComponentFailure } from 'domain/surveyDataManager/contracts/recipes';

import AvailableViewItem from './AvailableViewItem';
import { buildSdmPreconfiguredFavouriteKey } from './sdmFavouriteKeys';

export const AVAILABLE_VIEWS_PAGE_SIZE = 5;


export function measureCollapsedPagedListHeightPx(
  list: HTMLElement,
  pageSize: number,
): number {
  const items = Array.from(
    list.querySelectorAll(':scope > .sdm-available-views__item'),
  );
  if (items.length === 0) {
    return 0;
  }

  const listStyles = window.getComputedStyle(list);
  const gapPx =
    Number.parseFloat(listStyles.rowGap || listStyles.gap || '0') || 0;

  const itemHeights = items.map((item) => {
    if (!(item instanceof HTMLElement)) {
      return 0;
    }
    const row = item.querySelector('.sdm-available-views__item-row');
    const rowHeight =
      row instanceof HTMLElement ? row.getBoundingClientRect().height : 0;
    const styles = window.getComputedStyle(item);
    const paddingY =
      (Number.parseFloat(styles.paddingTop) || 0) +
      (Number.parseFloat(styles.paddingBottom) || 0);
    const borderY =
      (Number.parseFloat(styles.borderTopWidth) || 0) +
      (Number.parseFloat(styles.borderBottomWidth) || 0);
    const itemGap =
      Number.parseFloat(styles.rowGap || styles.gap || '0') || 0;
    const hasDetailsSlot =
      item.querySelector('.sdm-available-views__details-wrap') != null;
    return rowHeight + paddingY + borderY + (hasDetailsSlot ? itemGap : 0);
  });

  const measuredItemsHeight = itemHeights.reduce(
    (sum, height) => sum + height,
    0,
  );
  const measuredGaps = gapPx * Math.max(0, items.length - 1);
  const measured = measuredItemsHeight + measuredGaps;

  if (items.length >= pageSize) {
    return Math.ceil(measured);
  }

  const averageItemHeight =
    itemHeights.reduce((sum, height) => sum + height, 0) / items.length;
  return Math.ceil(
    averageItemHeight * pageSize + gapPx * Math.max(0, pageSize - 1),
  );
}

export interface AvailableViewGroupProps {
  readonly title: string;
  readonly description: string;
  readonly emptyTitle: string;
  readonly emptyBody: string;
  readonly views: readonly AvailableViewSummary[];
  readonly visibleViewIds: ReadonlySet<string>;
  readonly expandedViewId: string | null;
  readonly loadingViewIds: ReadonlySet<string>;
  readonly isCatalogueLoading?: boolean;
  readonly activationFailuresByViewId: Readonly<
    Record<string, readonly RecipeComponentFailure[]>
  >;
  readonly favouriteViewKeys: ReadonlySet<string>;
  readonly onToggleVisibility: (viewId: string) => void;
  readonly onToggleDetails: (viewId: string) => void;
  readonly onToggleFavourite: (view: AvailableViewSummary) => void;
  readonly defaultExpanded?: boolean;
  readonly preferExpanded?: boolean;
  readonly itemCount?: number;
  readonly children?: React.ReactNode;
  readonly pageSize?: number;
}

function AvailableViewGroup({
  title,
  description,
  emptyTitle,
  emptyBody,
  views,
  visibleViewIds,
  expandedViewId,
  loadingViewIds,
  isCatalogueLoading = false,
  activationFailuresByViewId,
  favouriteViewKeys,
  onToggleVisibility,
  onToggleDetails,
  onToggleFavourite,
  defaultExpanded = false,
  preferExpanded = false,
  itemCount,
  children,
  pageSize = AVAILABLE_VIEWS_PAGE_SIZE,
}: AvailableViewGroupProps): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded || preferExpanded);
  const [isBodyPainted, setIsBodyPainted] = useState(isExpanded);
  const [isBodyExpanded, setIsBodyExpanded] = useState(isExpanded);
  const [pageIndex, setPageIndex] = useState(0);
  const [slideDirection, setSlideDirection] = useState<'next' | 'prev' | null>(
    null,
  );
  const [lockedListHeightPx, setLockedListHeightPx] = useState<number | null>(
    null,
  );
  const listViewportRef = useRef<HTMLDivElement>(null);
  const wasExpandedRef = useRef(isExpanded);
  const panelId = useId();
  const hasViews = views.length > 0;
  const hasChildren = children != null;
  const totalItemCount = itemCount ?? views.length;
  const titleWithCount = `${title} (${totalItemCount})`;

  useEffect(() => {
    setIsExpanded(preferExpanded);
  }, [preferExpanded]);

  useLayoutEffect(() => {
    if (isExpanded) {
      setIsBodyPainted(true);
      return;
    }
    setIsBodyExpanded(false);
    if (
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      setIsBodyPainted(false);
    }
  }, [isExpanded]);

  useLayoutEffect(() => {
    if (!isExpanded || !isBodyPainted || isBodyExpanded) {
      return;
    }
    const frameId = requestAnimationFrame(() => {
      setIsBodyExpanded(true);
    });
    return () => cancelAnimationFrame(frameId);
  }, [isExpanded, isBodyPainted, isBodyExpanded]);

  useEffect(() => {
    if (isExpanded || !isBodyPainted) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      setIsBodyPainted(false);
    }, 250);
    return () => window.clearTimeout(timeoutId);
  }, [isExpanded, isBodyPainted]);

  const handleBodyTransitionEnd = (
    event: TransitionEvent<HTMLDivElement>,
  ): void => {
    if (event.target !== event.currentTarget) {
      return;
    }
    if (event.propertyName !== 'grid-template-rows') {
      return;
    }
    if (!isExpanded) {
      setIsBodyPainted(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(views.length / pageSize));
  const showPagination = !hasChildren && views.length > pageSize;

  const viewsIdentity = useMemo(
    () => views.map((view) => view.id).join('|'),
    [views],
  );

  useEffect(() => {
    setPageIndex(0);
    setSlideDirection(null);
    setLockedListHeightPx(null);
  }, [viewsIdentity]);

  useEffect(() => {
    if (isExpanded && !wasExpandedRef.current) {
      setLockedListHeightPx(null);
    }
    wasExpandedRef.current = isExpanded;
  }, [isExpanded]);

  useEffect(() => {
    setPageIndex((current) => Math.min(current, totalPages - 1));
  }, [totalPages]);

  const goToPage = (nextPage: number): void => {
    const clamped = Math.max(0, Math.min(totalPages - 1, nextPage));
    if (clamped === pageIndex) {
      return;
    }
    setSlideDirection(clamped > pageIndex ? 'next' : 'prev');
    setPageIndex(clamped);
  };

  const pageViews = useMemo(() => {
    const start = pageIndex * pageSize;
    return views.slice(start, start + pageSize);
  }, [pageIndex, pageSize, views]);

  useLayoutEffect(() => {
    if (!showPagination || !isExpanded || !isBodyExpanded) {
      return;
    }
    const viewport = listViewportRef.current;
    const list = viewport?.querySelector('.sdm-available-views__list');
    if (!(list instanceof HTMLElement)) {
      return;
    }
    // Collapsed-row measure keeps pagination fixed while Details scroll inside.
    const nextHeight = measureCollapsedPagedListHeightPx(list, pageSize);
    if (nextHeight <= 0) {
      return;
    }
    setLockedListHeightPx((current) =>
      current == null ? nextHeight : Math.max(current, nextHeight),
    );
  }, [
    isBodyExpanded,
    isExpanded,
    pageIndex,
    pageSize,
    pageViews,
    showPagination,
  ]);

  const rangeStart = views.length === 0 ? 0 : pageIndex * pageSize + 1;
  const rangeEnd = Math.min((pageIndex + 1) * pageSize, views.length);

  return (
    <section
      className={`sdm-available-views__group${
        isExpanded ? ' sdm-available-views__group--expanded' : ''
      }`}
      aria-label={titleWithCount}
    >
      <header className="sdm-available-views__group-header">
        <button
          type="button"
          className="sdm-available-views__group-toggle"
          aria-expanded={isExpanded}
          aria-controls={panelId}
          onClick={() => setIsExpanded((prev) => !prev)}
        >
          <span className="sdm-available-views__group-toggle-icon" aria-hidden>
            {isExpanded ? <HiChevronDown /> : <HiChevronRight />}
          </span>
          <span className="sdm-available-views__group-toggle-text">
            <span className="sdm-available-views__group-title">
              {titleWithCount}
            </span>
            <span className="sdm-available-views__group-description">
              {description}
            </span>
          </span>
        </button>
      </header>

      <div
        id={panelId}
        className={`sdm-available-views__group-body${
          isBodyExpanded ? ' sdm-available-views__group-body--expanded' : ''
        }`}
        hidden={!isBodyPainted}
        aria-hidden={!isExpanded}
        onTransitionEnd={handleBodyTransitionEnd}
      >
        <div className="sdm-available-views__group-body-inner">
          {hasChildren ? (
            children
          ) : hasViews ? (
            <>
              <div
                className={`sdm-available-views__paged${
                  showPagination
                    ? ' sdm-available-views__paged--with-pagination'
                    : ''
                }`}
              >
              <div
                ref={listViewportRef}
                className={`sdm-available-views__list-viewport${
                  showPagination
                    ? ' sdm-available-views__list-viewport--paged'
                    : ''
                }`}
                style={
                  showPagination && lockedListHeightPx != null
                    ? {
                        height: `${lockedListHeightPx}px`,
                        maxHeight: `${lockedListHeightPx}px`,
                      }
                    : undefined
                }
              >
                <ul
                  key={pageIndex}
                  className={`sdm-available-views__list${
                    slideDirection
                      ? ` sdm-available-views__list--slide-${slideDirection}`
                      : ''
                  }`}
                >
                  {pageViews.map((view) => {
                    return (
                      <AvailableViewItem
                        key={view.id}
                        view={view}
                        isVisible={visibleViewIds.has(view.id)}
                        isExpanded={expandedViewId === view.id}
                        isActivating={loadingViewIds.has(view.id)}
                        isCatalogueLoading={isCatalogueLoading}
                        activationFailures={
                          activationFailuresByViewId[view.id] ?? []
                        }
                        isFavourite={favouriteViewKeys.has(
                          buildSdmPreconfiguredFavouriteKey(view.id),
                        )}
                        onToggleVisibility={onToggleVisibility}
                        onToggleDetails={onToggleDetails}
                        onToggleFavourite={onToggleFavourite}
                      />
                    );
                  })}
                </ul>
              </div>
              {showPagination ? (
                <nav
                  className="sdm-available-views__pagination"
                  aria-label={`${titleWithCount} pagination`}
                >
                  <div className="sdm-available-views__pagination-controls">
                    <button
                      type="button"
                      className="sdm-available-views__pagination-button"
                      onClick={() => goToPage(0)}
                      disabled={pageIndex === 0}
                      aria-label={`First page of ${titleWithCount}`}
                    >
                      <HiChevronDoubleLeft aria-hidden />
                      <span>First</span>
                    </button>
                    <button
                      type="button"
                      className="sdm-available-views__pagination-button"
                      onClick={() => goToPage(pageIndex - 1)}
                      disabled={pageIndex === 0}
                      aria-label={`Previous page of ${titleWithCount}`}
                    >
                      <HiChevronLeft aria-hidden />
                      <span>Previous</span>
                    </button>
                  </div>
                  <p className="sdm-available-views__pagination-status" role="status">
                    <span className="sdm-available-views__pagination-range">
                      {rangeStart}–{rangeEnd} of {views.length}
                    </span>
                    <span className="sdm-available-views__pagination-page">
                      Page {pageIndex + 1} of {totalPages}
                    </span>
                  </p>
                  <div className="sdm-available-views__pagination-controls">
                    <button
                      type="button"
                      className="sdm-available-views__pagination-button"
                      onClick={() => goToPage(pageIndex + 1)}
                      disabled={pageIndex >= totalPages - 1}
                      aria-label={`Next page of ${titleWithCount}`}
                    >
                      <span>Next</span>
                      <HiChevronRight aria-hidden />
                    </button>
                    <button
                      type="button"
                      className="sdm-available-views__pagination-button"
                      onClick={() => goToPage(totalPages - 1)}
                      disabled={pageIndex >= totalPages - 1}
                      aria-label={`Last page of ${titleWithCount}`}
                    >
                      <span>Last</span>
                      <HiChevronDoubleRight aria-hidden />
                    </button>
                  </div>
                </nav>
              ) : null}
              </div>
            </>
          ) : (
            <div className="sdm-available-views__empty" role="note">
              <p className="sdm-available-views__empty-title">{emptyTitle}</p>
              <p className="sdm-available-views__empty-body">{emptyBody}</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default AvailableViewGroup;
